import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { TeamLogo } from '@/lib/teamLogos';
import { STATUS_COLORS } from './constants';

export function RequestInfoCard({ request }) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50">
        <div>
          <p className="text-xs text-gray-400">Request {request.request_number ? `#${request.request_number}` : ''}</p>
          <p className="text-xs text-gray-400">{request.submitted_at?.split('T')[0]}</p>
        </div>
        <div className="flex items-center gap-2">
          {request.picked_up_by_name && (
            <Badge className="text-[10px] px-2 py-0.5 h-5 font-medium bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800 gap-1.5">
              <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-blue-600 text-[7px] font-bold text-white shrink-0">
                {request.picked_up_by_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
              </span>
              {request.picked_up_by_name}
            </Badge>
          )}
          {(request.season || request.match?.season) && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-medium border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
              {request.season || request.match?.season}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">{t('match_info')}</p>
            {request.round && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-medium bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">
                Round {request.round}
              </Badge>
            )}
          </div>
          {request.match ? (
            <>
              <div className="flex items-center gap-2">
                <TeamLogo name={request.match?.home_team} badgeUrl={request.match?.home_team_badge} size={24} />
                <span className="font-medium text-gray-800 text-sm">{request.match?.home_team}</span>
                <span className="text-gray-300 text-xs mx-1">vs</span>
                <TeamLogo name={request.match?.away_team} badgeUrl={request.match?.away_team_badge} size={24} />
                <span className="font-medium text-gray-800 text-sm">{request.match?.away_team}</span>
              </div>
              <p className="text-gray-400 text-xs mt-2">{request.match?.match_date} at {request.match?.kickoff_time}</p>
              <p className="text-gray-400 text-xs">{request.match?.stadium}{request.match?.city ? `, ${request.match.city}` : ''}</p>
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No Match</p>
          )}
        </div>
        <div className="p-5">
          <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-3">{t('sponsor_info')}</p>
          <div className="flex items-center gap-3">
            {request.sponsor?.logo_url ? (
              <img src={request.sponsor.logo_url} alt="" className="h-10 w-10 rounded-lg object-cover border border-gray-100" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-400">
                {request.sponsor?.company_name?.[0]}
              </div>
            )}
            <div>
              <p className="font-medium text-gray-800 text-sm">{request.sponsor?.company_name}</p>
              {request.created_by_name && (
                <p className="text-gray-400 text-xs" data-testid="detail-requester">
                  {t('requested_by')}: <span className="text-gray-600 dark:text-gray-300 font-medium">{request.created_by_name}</span>
                  {request.created_by_org && (
                    <span className={request.created_by_org === 'INCEPTION'
                      ? 'text-amber-600 dark:text-amber-500 font-semibold tracking-wide'
                      : 'text-gray-500 dark:text-gray-400'}> · {request.created_by_org}</span>
                  )}
                </p>
              )}
            </div>
          </div>
          {request.sponsor_note && <p className="text-gray-400 text-xs italic mt-2">{t('note')}: {request.sponsor_note}</p>}
        </div>
      </div>
    </>
  );
}
